import { createClient } from '@supabase/supabase-js';
import { supabaseConfig } from '../constants';

// Initialize Supabase client
// Uses REACT_APP_ prefixed vars (required for CRA) or falls back to non-prefixed
if (!supabaseConfig.url || !supabaseConfig.anonKey) {
  console.error('Supabase configuration missing! Check environment variables:', {
    url: supabaseConfig.url ? 'Set' : 'MISSING',
    anonKey: supabaseConfig.anonKey ? 'Set' : 'MISSING',
    envVars: {
      REACT_APP_SUPABASE_URL: process.env.REACT_APP_SUPABASE_URL ? 'Set' : 'MISSING',
      SUPABASE_URL: process.env.SUPABASE_URL ? 'Set' : 'MISSING',
      REACT_APP_SUPABASE_ANON_KEY: process.env.REACT_APP_SUPABASE_ANON_KEY ? 'Set' : 'MISSING',
      SUPABASE_ANON_KEY: process.env.SUPABASE_ANON_KEY ? 'Set' : 'MISSING',
    }
  });
}

export const supabase = createClient(
  supabaseConfig.url || 'https://placeholder.supabase.co',
  supabaseConfig.anonKey || 'placeholder-key'
);

// Authentication helpers
export const signInAnonymously = async () => {
  try {
    // Sign in anonymously with Supabase Auth
    const { data, error } = await supabase.auth.signInAnonymously();
    if (error) throw error;
    
    const authUserId = data.user?.id;
    if (!authUserId) return null;

    // Check if user exists in users table, create if not
    const { data: existingUser, error: userError } = await supabase
      .from('users')
      .select('id')
      .eq('id', authUserId)
      .single();

    if (userError && userError.code === 'PGRST116') {
      // User doesn't exist in users table, create one
      const { error: createError } = await supabase
        .from('users')
        .insert({
          id: authUserId,
          name: 'Anonymous User',
          created_at: new Date().toISOString(),
        });

      if (createError) {
        console.error('Error creating user:', createError);
      }
    }

    return authUserId;
  } catch (error) {
    console.error('Supabase Auth Error:', error);
    return null;
  }
};

export const signInWithToken = async (token) => {
  try {
    const { data, error } = await supabase.auth.getUser(token);
    if (error) throw error;
    return data.user?.id || null;
  } catch (error) {
    console.error('Supabase Token Auth Error:', error);
    return null;
  }
};

// Get or create user in users table
export const getOrCreateUser = async (authUserId) => {
  if (!authUserId) return null;

  try {
    // Check if user exists
    const { data: user, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', authUserId)
      .single();

    if (error && error.code === 'PGRST116') {
      // User doesn't exist, create one
      const { data: newUser, error: createError } = await supabase
        .from('users')
        .insert({
          id: authUserId,
          name: 'New User',
          created_at: new Date().toISOString(),
          is_admin: false, // New users are not admins by default
        })
        .select()
        .single();

      if (createError) {
        console.error('Error creating user:', createError);
        return null;
      }

      return newUser;
    }

    if (error) {
      console.error('Error fetching user:', error);
      return null;
    }

    return user;
  } catch (error) {
    console.error('Error in getOrCreateUser:', error);
    return null;
  }
};

// Database helpers
export const getCurrentUser = () => {
  return supabase.auth.getUser();
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
};
